import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CommunityType,
  EditCommunityDocument,
  GetAllCommunityRepostsDocument,
  Maybe,
  SetHidePostsDocument,
} from '~/api/generated/graphql'
import { useNavigate } from 'react-router'
import { Button, Dropdown, Modal } from 'react-bootstrap'
import PlainTextInput from '~/common/PlainTextInput'
import FormError from '~/common/FormError'
import { upperCaseFirst } from 'change-case-all'
import TextListSelector from '~/common/TextListSelector'
import CommunityPhotoEdit from '~/pages/community/CommunityPhotoEdit'
import { CommunitySettingsDataModel } from '~/types'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import { elementClicked } from '~/common/EventLogger'
import { useAuth } from '~/auth/Auth'
import { useMutation, useQuery } from '@apollo/client'
import { usePrompt } from '~/contexts/PromptContext'

type Props = {
  community?: CommunitySettingsDataModel
  isLeader?: boolean
  profileVisible: boolean
}

const MAX_ALIAS_LENGTH = 100

const CommunitySettingsEdit = ({ community, isLeader, profileVisible }: Props) => {
  const navigate = useNavigate()
  const [update] = useMutation(EditCommunityDocument)
  const [hidePosts] = useMutation(SetHidePostsDocument)

  const { actingSysAdmin } = useAuth()

  const { isCondensed } = useWindowSize()
  const condensedClass = isCondensed ? ' condensed' : ''
  const [pendingName, setPendingName] = useState<string>(community?.name ?? '')
  const [pendingDescription, setPendingDescription] = useState<Maybe<string>>(community?.description ?? '')
  const [pendingAliasList, setPendingAliasList] = useState<string[]>(community?.aliases ?? [])
  const [duplicateNameError, setDuplicateNameError] = useState(false)
  const [uploadId, setUploadId] = useState<string>()
  const [uploading, setUploading] = useState(false)
  const [removePhoto, setRemovePhoto] = useState<boolean>()
  const [pendingPhoto, setPendingPhoto] = useState<string>(community?.photo ?? '')
  const [pendingType, setPendingType] = useState<Maybe<CommunityType>>(community?.type || CommunityType.Public)
  const [showTypeModal, setShowTypeModal] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [communityNameError, setCommunityNameError] = useState<boolean>(false)

  const topCancelBtnRef = useRef<HTMLButtonElement>(null)
  const bottomCancelBtnRef = useRef<HTMLButtonElement>(null)

  const [pendingAlias, setPendingAlias] = useState<string>('')

  const aliasListModified = useMemo(() => {
    return (
      community?.aliases?.length !== pendingAliasList?.length ||
      !pendingAliasList?.every(item => community?.aliases?.includes(item))
    )
  }, [pendingAliasList, community?.aliases])

  const noChanges =
    community?.name === pendingName &&
    (community?.description === pendingDescription || (!community?.description && !pendingDescription)) &&
    (!removePhoto || (!community?.photo && !uploadId)) &&
    !uploadId &&
    !aliasListModified &&
    pendingType === community?.type

  const { block, unblock } = usePrompt()
  const allowNavigation = useCallback(() => unblock('cse'), [unblock])

  useEffect(() => {
    if (noChanges) {
      allowNavigation()
    } else {
      block({ key: 'cse', priority: 1, message: 'You have unsaved changes. Are you sure you want to leave?' })
    }
  }, [noChanges, block, unblock, allowNavigation])

  const { data: repostData } = useQuery(GetAllCommunityRepostsDocument, {
    variables: { communityId: community?.communityId ?? '' },
    skip: !community?.communityId,
  })
  const repostIds =
    repostData?.community?.posts?.edges.flatMap(
      post => post?.node?.reposts?.flatMap(repost => repost?.postId ?? null) ?? []
    ) ?? []

  const returnHome = useCallback(() => {
    void navigate(`../about`)
  }, [navigate])

  const handleCancel = () => {
    topCancelBtnRef.current?.blur()
    bottomCancelBtnRef.current?.blur()
    allowNavigation()
    // Allow current thread updates to complete so that update of state is reflected in the prompt context before
    // trying to navigate. Otherwise, the blocker will not have updated yet and the navigation will be blocked
    // despite the message being empty.
    setTimeout(() => {
      returnHome()
    })
  }

  const handleSave = async (e: SyntheticEvent) => {
    if (community?.type === pendingType) {
      if (pendingName == '') {
        setCommunityNameError(true)
      }

      if (pendingName) {
        allowNavigation()
        elementClicked(e, 'click-community-settings-save', { community_id: community?.communityId })
        await doSave()
      }
    } else {
      setShowTypeModal(true)
    }
  }
  const doSave = async () => {
    if (community?.communityId) {
      const resp = await update({
        variables: {
          communityId: community?.communityId,
          name: pendingName.trim(),
          description: pendingDescription?.trim(),
          uploadId,
          deleteMedia: removePhoto,
          communityType: actingSysAdmin ? pendingType : undefined,
          aliases: pendingAliasList.concat(pendingAlias ? [pendingAlias.trim()] : []),
        },
      })

      if (resp.data?.editCommunity?.ok) {
        if (pendingType === CommunityType.Private && (repostIds?.length ?? 0 > 0)) {
          await hidePosts({ variables: { ids: repostIds, value: true } })
        }
        returnHome()
      } else {
        switch (resp.data?.editCommunity?.error?.code) {
          case 'duplicateName':
            setDuplicateNameError(true)
            break
          default:
            setError(`Error updating community. Please try again later.`)
            break
        }
      }
    }
  }

  const handleRemovePhoto = (remove: boolean) => {
    setRemovePhoto(remove)
    setPendingPhoto('')
  }

  const handleTypeCancel = () => {
    setShowTypeModal(false)
  }

  const handleTypeConfirm = async (e: SyntheticEvent) => {
    setShowTypeModal(false)
    allowNavigation()
    elementClicked(e, 'click-community-type-change-confirm', { community_id: community?.communityId })
    await doSave()
  }

  const nameLength = pendingName?.length ?? 0
  const descriptionLength = pendingDescription?.length ?? 0
  const aliasLength = pendingAlias?.length ?? 0

  const maxDescriptionLength = 150
  const descriptionSeverity =
    descriptionLength >= maxDescriptionLength ? ' red' : descriptionLength > 100 ? ' yellow' : ' gone'

  const maxNameLength = 133
  const nameSeverity = nameLength > maxNameLength ? ' red' : nameLength > 100 ? ' yellow' : ' gone'
  const inputExceeded =
    nameLength > maxNameLength || descriptionLength > maxDescriptionLength || aliasLength > MAX_ALIAS_LENGTH

  return (
    <div className={'settings-container'}>
      <div className="top-items">
        <h3 className="comm-title">{`${community?.name} Settings`}</h3>
        {isLeader && (
          <span className={'button-options'}>
            <Button variant="light" onClick={handleCancel} ref={topCancelBtnRef}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!profileVisible || inputExceeded || uploading} onClick={handleSave}>
              Save
            </Button>
          </span>
        )}
      </div>
      <div className="community-settings">
        <div className="left-col">
          <div className="authoring-area">
            <div className="authoring-form">
              <h3 className="section-title">Name*</h3>
              {communityNameError ? <span className={'value-message'}>Required</span> : ''}
              <PlainTextInput
                placeholder={`Add a Community Name`}
                onChange={setPendingName}
                value={pendingName}
                className={`name-editor ${communityNameError ? 'empty-error' : ''}`}
                disabled={!profileVisible}
                onKeyDown={() => {
                  setDuplicateNameError(false)
                  setCommunityNameError(false)
                }}
              />
              <span className={'character-counter name' + nameSeverity + condensedClass}>
                {nameLength < 10 ? `0${nameLength}` : nameLength} / {maxNameLength}
              </span>
              {duplicateNameError && <FormError message={'A community already exists with that name'} />}
            </div>
          </div>
          <div className="authoring-area">
            <div className="authoring-form">
              <h3 className="section-title">Description</h3>
              <PlainTextInput
                placeholder={'Add a Description'}
                onChange={setPendingDescription}
                value={pendingDescription}
                className={'description-editor'}
                disabled={!profileVisible}
              />
              <span className={'character-counter description' + descriptionSeverity + condensedClass}>
                {descriptionLength < 10 ? `0${descriptionLength}` : descriptionLength} / {maxDescriptionLength}
              </span>
            </div>
          </div>
          <div className="authoring-area visibility">
            <div className="authoring-form">
              <span className="section-title">Visibility</span>
              <Dropdown
                id="dropdown-type"
                data-testid="type-select"
                title={actingSysAdmin ? '' : 'Only admins may change visibility'}
              >
                <Dropdown.Toggle id="dropdown-basic" role={'dropdown'} disabled={!actingSysAdmin}>
                  {upperCaseFirst(String(pendingType).toLowerCase())}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    key="Public"
                    onClick={() => setPendingType(CommunityType.Public)}
                    disabled={!profileVisible}
                  >
                    Public
                  </Dropdown.Item>
                  <Dropdown.Item
                    key="Private"
                    onClick={() => setPendingType(CommunityType.Private)}
                    disabled={!profileVisible}
                  >
                    Private
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="authoring-area aliases">
            <div className="authoring-form">
              <h3>Aliases</h3>
              <span>Other names that may be used to search for {community?.name}.</span>
              <div>
                <TextListSelector
                  placeholder="Add alias"
                  list={pendingAliasList ?? []}
                  onUpdateList={setPendingAliasList}
                  maxItemLength={MAX_ALIAS_LENGTH}
                  mediumSeverityItemLength={70}
                  duplicateErrorMessage="That alias already exists"
                  addMessage="Add new alias"
                  disabled={!profileVisible}
                  setPendingInput={setPendingAlias}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="right-col">
          <span className="section-title">Photo</span>
          <CommunityPhotoEdit
            existingPhoto={pendingPhoto}
            communityName={community?.name}
            setRemovePhoto={handleRemovePhoto}
            setUploadId={setUploadId}
            disabled={!profileVisible}
            uploading={uploading}
            setUploading={setUploading}
          />
        </div>
      </div>
      <div className="bottom-items">
        {isLeader && (
          <span>
            <Button variant="light" onClick={handleCancel} ref={bottomCancelBtnRef}>
              Cancel
            </Button>
            <Button
              data-testid={'community-settings-save-btn'}
              variant="primary"
              disabled={!profileVisible || inputExceeded || uploading}
              onClick={handleSave}
            >
              Save
            </Button>
          </span>
        )}
      </div>
      <Modal show={showTypeModal} onHide={handleTypeCancel} data-testid="type-modal">
        <Modal.Header closeButton />
        <Modal.Body>
          {`Are you sure you want to change this community from ${community?.type} to ${pendingType}?${
            pendingType === CommunityType.Private ? ' This will delete any reposts from this community.' : ''
          }`}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleTypeCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleTypeConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!error} onHide={() => setError(null)}>
        <Modal.Body>{error}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" size="sm" onClick={() => setError(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default CommunitySettingsEdit
